import React, { useEffect, useState } from 'react';
import { useAppStore } from './store/app-store';
import { initializeCloudflareClient } from './lib/cloudflare-client';
import { initializeUnifiedClient } from './lib/unified-model-client';
import { usePersistence } from './hooks/usePersistence';
import { Sidebar } from './components/Sidebar';
import { UnifiedChat } from './components/UnifiedChat';
import { Settings } from './components/Settings';
import { ConfigModal } from './components/ConfigModal';
import { ModelSelector } from './components/ModelSelector';
import { Connectors } from './components/Connectors';
import { AutonomousMode } from './components/AutonomousMode';
import './App.css';

function App() {
  const {
    cloudflare,
    ollama,
    selectedModel,
    setSelectedModel,
    ui,
    toggleSidebar,
    toggleSettings,
    getActiveConversation
  } = useAppStore();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [showConnectors, setShowConnectors] = useState(false);
  const [showAutonomousMode, setShowAutonomousMode] = useState(false);

  // Initialize persistence (auto-load conversations from IndexedDB)
  const { isInitialized } = usePersistence();

  useEffect(() => {
    // Always try to initialize with env vars if available
    const workerUrl = cloudflare.workerUrl || import.meta.env.VITE_WORKER_URL;
    const accountId = cloudflare.accountId || import.meta.env.VITE_CF_ACCOUNT_ID;
    const apiToken = cloudflare.apiToken || import.meta.env.VITE_CF_API_TOKEN;
    const ollamaUrl = ollama.baseUrl || import.meta.env.VITE_OLLAMA_BASE_URL;

    if (workerUrl && accountId && apiToken) {
      initializeCloudflareClient(accountId, apiToken, workerUrl);

      // Initialize unified client with both providers
      initializeUnifiedClient({
        ollamaBaseUrl: ollamaUrl,
        cloudflareWorkerUrl: workerUrl,
        cloudflareAccountId: accountId,
        cloudflareApiToken: apiToken
      });

      console.log('[App] ✅ Initialized with credentials');
    } else {
      console.warn('[App] ⚠️ Missing credentials, showing config modal');
      setShowConfigModal(true);
    }
  }, [cloudflare.isConfigured, ollama.baseUrl]);

  const conversation = getActiveConversation();

  return (
    <div className="app">
      {/* Header */}
      <header className="app-header">
        <button className="menu-btn" onClick={toggleSidebar}>
          ☰
        </button>
        <h1 className="app-title">
          {conversation?.title || 'Unified Dev Agent'}
        </h1>
        <div className="header-actions">
          <button
            className="autonomous-btn"
            onClick={() => setShowAutonomousMode(!showAutonomousMode)}
            title="Autonomous Development Mode"
          >
            🤖 Auto-Build
          </button>
          <button
            className="icon-btn"
            onClick={() => setShowModelSelector(!showModelSelector)}
            title="Select Model"
          >
            🤖 {selectedModel?.split(':')[0] || 'Model'}
          </button>
          <button
            className="icon-btn"
            onClick={() => setShowConnectors(!showConnectors)}
            title="API Connectors"
          >
            🔌
          </button>
          <button
            className="icon-btn"
            onClick={() => setShowConfigModal(true)}
            title="Configure"
          >
            ⚙️
          </button>
          <button
            className="icon-btn"
            onClick={toggleSettings}
            title="Settings"
          >
            🔧
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="app-body">
        {ui.sidebarOpen && <Sidebar />}
        <main className="main-content">
          {showAutonomousMode ? <AutonomousMode /> : showConnectors ? <Connectors /> : <UnifiedChat />}
        </main>
        {ui.settingsOpen && <Settings />}
      </div>

      {/* Modals */}
      {showConfigModal && (
        <ConfigModal onClose={() => setShowConfigModal(false)} />
      )}

      {showModelSelector && (
        <div className="modal-overlay" onClick={() => setShowModelSelector(false)}>
          <div className="modal-content model-selector-modal" onClick={(e) => e.stopPropagation()}>
            <ModelSelector
              selectedModel={selectedModel}
              onModelChange={(modelId) => {
                setSelectedModel(modelId);
                setShowModelSelector(false);
              }}
            />
            <button
              className="close-modal-btn"
              onClick={() => setShowModelSelector(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
